import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Stack, Tabs, Tab, Box, FormControl,
  InputLabel, Select, MenuItem, Typography, Divider, CircularProgress,
  FormHelperText, Backdrop, Tooltip
} from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enGB } from "date-fns/locale";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

import { SERVER_URL } from "../../constants";
import { BOOKING_STATUS_OPTIONS } from "./utils/bookingStatus";
import { fetchJson } from "./utils/http";
import GroupAgentSection from "./GroupAgentSection";
import { fmtDate } from "./utils/dates";
import MembersTab from "./MembersTab";
import StudentDetailsTab from "./StudentDetailsTab";
import DangerZone from "./DangerZone";

import { useMembers } from "./hooks/useMembers";
import { useEditableStudents } from "./hooks/useEditableStudents";
import { useBookingMeta } from "./hooks/useBookingMeta";

import WeeklyActivitiesTab from "./WeeklyActivitiesTab";
import StaysTab from "./StaysTab";
import BookingsTab from "./BookingsTab";
import GroupFinanceTab from "./GroupFinanceTab";
import { useStudentDisplayList } from "./hooks/useStudentDisplayList";
import { useAdultDisplayList } from "./hooks/useAdultDisplayList";
import { deleteStudentsBatch, deleteAdultsBatch, alignBookings } from "./api/people";

import AdultDetailsTab from "./AdultDetailsTab";
import { useEditableAdults } from "./hooks/useEditableAdults";

import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

// The explicit "Align Bookings" reconcile still calls the backend align-bookings,
// which currently blanket-books every WEEKEND-type product for the campus/window
// (the Murrayfield-mass-applied-to-Loretto bug). Keep the button disabled until the
// backend ships "one excursion per weekend"; flip this to true to re-enable.
const ALIGN_BOOKINGS_ENABLED = false;

export default function EditBookingGroup({
  open,
  onClose,
  group,            // { id, groupName, campus, notes, status, studentIds, adultIds }
  onUpdated,        // callback to refresh parent list
  onMembersChanged,
  campuses = [],    // optional [{id,name}]
  showFinancials = false,
  initialTab = "details", // e.g. "members" when opened from the Gantt
}) {

  const bookingSettingsTouchedRef = useRef(false);
  const token = sessionStorage.getItem("bearer");
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));

  // basic fields
  const [groupName, setGroupName] = useState(group?.groupName || "");
  const [notes, setNotes] = useState(group?.notes || "");

  // campus dropdown helpers
  const campusNames = useMemo(() => (campuses || []).map((c) => c.name), [campuses]);
  const isValidCampus = (name) => campusNames.includes(name);
  const [campusError, setCampusError] = useState("");

  // booking meta (loads/upserts campgroup meta & sync campus)
  const { loading: metaLoading, meta, setMeta, save: saveMeta } = useBookingMeta({ group, token });

  // members hook: owns tab state (to avoid flicker) + add/remove + lazy refresh
  const {
    tab, setTab, loading: loadingMembers,
    studentIds, adultIds, studentDetails, adultDetails,
    refresh, reloadMembership, addStudents, removeStudent, addAdults, removeAdult, addNewStudents, addNewAdults,
    stayAlerts, clearStayAlerts, pruneStudents, pruneAdults
  } = useMembers({
    groupId: group.id,
    token,
    initialTab,
    initialStudentIds: Array.isArray(group?.studentIds) ? group.studentIds : [],
    initialAdultIds: Array.isArray(group?.adultIds) ? group.adultIds : [],
  });

  useEffect(() => {
    if (!open) return;
    refresh();
  }, [open, group?.id]);

  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDirty(false); // fresh session each time dialog opens
  }, [open, group?.id]);

  const handleClose = () => {
    // If anything changed during this edit session, tell parent to refetch.
    if (dirty) onUpdated?.();
    onClose?.();
  };

  // student editing hook — we patch by refreshing the list after saves
  const editable = useEditableStudents({
    token,
    onListPatched: () => refresh(),
  });

  const editableAdults = useEditableAdults({
    token,
    onListPatched: () => refresh(),
  });

   // Students list for tabs that only need IDs (like WeeklyActivities)
  const studentsForActivities = useMemo(
    () =>
      studentDetails.length > 0
        ? studentDetails
        : (studentIds || []).map((id) => ({ id })),
    [studentDetails, studentIds]
  );

  const { students: studentsForDangerZone, loading: dzLoading, invalidate } =
    useStudentDisplayList(studentIds, token, tab === "danger");

  const { adults: adultsForDangerZone, loading: dzAdultsLoading, invalidate: invalidateAdults } =
    useAdultDisplayList(adultIds, token, tab === "danger");

  // quick inputs for add by ID
  const [addStudentsInput, setAddStudentsInput] = useState("");
  const [addAdultsInput, setAddAdultsInput] = useState("");

  const canSave = useMemo(() => groupName.trim().length > 0, [groupName]);

  // NEW: count for new placeholders
  const [newStudentsCount, setNewStudentsCount] = useState("");
  const [newAdultsCount, setNewAdultsCount] = useState("");
  const [creatingStudents, setCreatingStudents] = useState(false);
  const [creatingAdults, setCreatingAdults] = useState(false);

  // confirmation dialog for bulk create
  const [confirmCreateOpen, setConfirmCreateOpen] = useState(false);
  const [confirmCreateType, setConfirmCreateType] = useState(null); // "students" | "adults"
  const [confirmCreateCount, setConfirmCreateCount] = useState(0);

  // full-page overlay during bulk operations
  const [bulkOperationActive, setBulkOperationActive] = useState(false);
  const [bulkOperationStatus, setBulkOperationStatus] = useState("");
  const [bulkElapsed, setBulkElapsed] = useState(0);

  const flightBaselineRef = useRef(null);        // snapshot when dialog opens
  const flightAppliedRef = useRef(null);         // snapshot when we last applied
  const [flightSavedNotApplied, setFlightSavedNotApplied] = useState({ in: false, out: false });

  // lightweight toast + queue
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [toastSeverity, setToastSeverity] = useState("info");
  const [hasEverAligned, setHasEverAligned] = useState(false);
  const [aligning, setAligning] = useState(false);
  const [alignmentSummary, setAlignmentSummary] = useState(null);

  const toastOnce = useCallback((message, severity = "info") => {
    setToastMsg(message);
    setToastSeverity(severity);
    setToastOpen(true);
  }, []);

  const toastFinal = toastOnce;

  // ───────────────────────────────────────────────
  // Group flight "template vs applied" helpers
  // ───────────────────────────────────────────────
  const snapshotFromMeta = (m) => ({
    in: {
      date: m.inTransferDate,
      flightId: m.inFlightId || "",
      depart: m.inDepart || "",
      arrive: m.inArrive || "",
      time: m.inArrivalTime || "",
      notes: m.inNotes || "",
    },
    out: {
      date: m.outTransferDate,
      flightId: m.outFlightId || "",
      depart: m.outDepart || "",
      arrive: m.outArrive || "",
      time: m.outDepartureTime || "",
      notes: m.outNotes || "",
    },
  });

  const equal = (a, b) => String(a || "").trim() === String(b || "").trim();

  const sameDate = (a, b) =>
  (a instanceof Date ? a.getTime() : null) === (b instanceof Date ? b.getTime() : null);

  const [lastAlignedMeta, setLastAlignedMeta] = useState(null);
  
  const metaKeysForAlignment = [
    "defaultCampus",
    "defaultStartDate",
    "defaultEndDate",
    "defaultNationality",
    "defaultRate",
  ];

  useEffect(() => {
    if (!open) return;
    setLastAlignedMeta(null);
    setHasEverAligned(false);
    setAlignmentSummary(null);
    bookingSettingsTouchedRef.current = false;
  }, [open, group?.id]);

  const snapshotForAlignment = (m) => ({
    defaultCampus: m.defaultCampus ?? "",
    defaultStartDate: m.defaultStartDate instanceof Date ? m.defaultStartDate.getTime() : (m.defaultStartDate ?? null),
    defaultEndDate: m.defaultEndDate instanceof Date ? m.defaultEndDate.getTime() : (m.defaultEndDate ?? null),
    defaultNationality: m.defaultNationality ?? "",
      defaultRate: m.defaultRate ?? "",
});

  const metaDiffersFrom = (currentMeta, baselineSnap) => {
    if (!currentMeta || !baselineSnap) return false;

    const cur = snapshotForAlignment(currentMeta);

    return metaKeysForAlignment.some((k) => (cur[k] ?? "") !== (baselineSnap[k] ?? ""));
  };

  useEffect(() => {
    if (!open) return;
    if (metaLoading) return;

    const snap = {
      in: {
        date: meta.inTransferDate,
        flightId: meta.inFlightId || "",
        depart: meta.inDepart || "",
        arrive: meta.inArrive || "",
        time: meta.inArrivalTime || "",
        notes: meta.inNotes || "",
      },
      out: {
        date: meta.outTransferDate,
        flightId: meta.outFlightId || "",
        depart: meta.outDepart || "",
        arrive: meta.outArrive || "",
        time: meta.outDepartureTime || "",
        notes: meta.outNotes || "",
      },
    };

    flightBaselineRef.current = snap;
    flightAppliedRef.current = { in: snap.in, out: snap.out };       // assume transfers match on open
    setFlightSavedNotApplied({ in: false, out: false });
  }, [open, metaLoading, group?.id]);

  const metaNeedsAlignment = useMemo(
    () =>
      !!lastAlignedMeta &&
      !metaLoading &&
      bookingSettingsTouchedRef.current &&
      metaDiffersFrom(meta, lastAlignedMeta),
    [meta, lastAlignedMeta, metaLoading]
  );
  
  // parse and call add handlers — open confirmation dialog instead of creating immediately
  const handleAddNewStudents = () => {
    const count = Number(newStudentsCount);
    if (!Number.isInteger(count) || count <= 0) return;
    if (creatingStudents) return;
    setConfirmCreateType("students");
    setConfirmCreateCount(count);
    setConfirmCreateOpen(true);
  };

  const handleAddNewAdults = () => {
    const count = Number(newAdultsCount);
    if (!Number.isInteger(count) || count <= 0) return;
    if (creatingAdults) return;
    setConfirmCreateType("adults");
    setConfirmCreateCount(count);
    setConfirmCreateOpen(true);
  };

  const handleConfirmCreate = async () => {
    setConfirmCreateOpen(false);
    setBulkOperationActive(true);

    const type = confirmCreateType;
    const count = confirmCreateCount;
    const onProgress = (msg) => setBulkOperationStatus(msg);

    try {
      if (type === "students") {
        setCreatingStudents(true);
        setBulkOperationStatus(`Creating ${count} student${count === 1 ? "" : "s"}…`);
        await addNewStudents(count, meta, { onProgress });
        setNewStudentsCount("");
        setDirty(true);
        toastOnce(`Created ${count} student${count === 1 ? "" : "s"}.`, "success");
        setCreatingStudents(false);
      } else {
        setCreatingAdults(true);
        setBulkOperationStatus(`Creating ${count} adult${count === 1 ? "" : "s"}…`);
        await addNewAdults(count, meta, {
          groupName: groupName?.trim() || group?.groupName || "Group Leader",
        }, { onProgress });
        setNewAdultsCount("");
        setDirty(true);
        toastOnce(`Created ${count} adult${count === 1 ? "" : "s"}.`, "success");
        setCreatingAdults(false);
      }

      // Auto-booking retired: creating members no longer auto-aligns bookings.
      // Bookings are now created/removed only by deliberate action on the Bookings
      // tab (or the explicit "Align Bookings" reconcile button).
      setBulkOperationStatus("Done!");
      toastOnce("All operations complete.", "success");
      setBulkOperationActive(false);
      setBulkOperationStatus("");
    } catch (e) {
      console.error("Bulk create failed", e);
      toastOnce(`Operation failed: ${e.message}`, "error");
      setCreatingStudents(false);
      setCreatingAdults(false);
      setBulkOperationActive(false);
      setBulkOperationStatus("");
    }
  };

  // Elapsed-time counter while bulk overlay is visible
  useEffect(() => {
    if (!bulkOperationActive) { setBulkElapsed(0); return; }
    const t = setInterval(() => setBulkElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [bulkOperationActive]);

  const handleRemoveStudent = async (id) => {
    await removeStudent(id);
    setDirty(true);
  };

  const handleRemoveAdult = async (id) => {
    await removeAdult(id);
    setDirty(true);
  };

  // re-sync basic fields and quick-inputs whenever a new group is opened
  useEffect(() => {
    if (!open) return;
    setGroupName(group?.groupName || "");
    setNotes(group?.notes || "");
    setAddStudentsInput("");
    setAddAdultsInput("");
  }, [open, group?.id]);

  // when useMembers emits stayAlerts, enqueue and clear in the hook
  // --- stay alert aggregation (debounced) ---
  const stayAggRef = useRef({
    count: 0,
    firstMsg: null,
    timer: null,
  });

  useEffect(() => {
    if (!stayAlerts?.length) return;

    // accumulate
    const agg = stayAggRef.current;
    agg.count += stayAlerts.length;
    agg.firstMsg = agg.firstMsg || stayAlerts[0]?.message || null;

    // clear the hook's list immediately so it doesn't re-trigger
    clearStayAlerts();

    // debounce: wait briefly for more batches to arrive
    if (agg.timer) clearTimeout(agg.timer);

    agg.timer = setTimeout(() => {
      const total = agg.count;
      const first = agg.firstMsg || "";

      const campusMatch = first.match(/\((.*?)\s+\d{4}-/);
      const rangeMatch = first.match(/(\d{4}-\d{2}-\d{2}).*(\d{4}-\d{2}-\d{2})/);

      const campus = campusMatch ? campusMatch[1] : "";
      const range = rangeMatch ? `${rangeMatch[1]} → ${rangeMatch[2]}` : "";

      const summary = `${total} stay${total === 1 ? "" : "s"} created${campus ? ` for ${campus}` : ""}${range ? ` (${range})` : ""}.`;
      toastOnce(summary, "info");
      // reset aggregator
      stayAggRef.current = { count: 0, firstMsg: null, timer: null };
    }, 800); // <-- tune: 500-1200ms works well
  }, [stayAlerts, clearStayAlerts]);

  
  
  useEffect(() => {
    if (!open) return;
    if (metaLoading) return;

    // Set baseline once (don’t overwrite after user edits)
    setLastAlignedMeta((prev) => prev ?? snapshotForAlignment(meta));
  }, [open, metaLoading, group?.id, meta]);

  // Save details + booking meta
  const handleSaveDetails = async () => {
    if (!canSave) return;

    // campus validation
    if (!isValidCampus(meta.defaultCampus)) {
      setCampusError("Please select a campus from the list.");
      return;
    }
    setCampusError("");

    try {
      // ✅ capture snapshot BEFORE saving (prevents stale meta issues)
      const snapBeforeSave = snapshotFromMeta(meta);

      await saveMeta({
        groupName,
        notes,
        studentIdsForStaySync: studentIds,
        adultIdsForStaySync: adultIds,
      });

      setDirty(true);

      toastOnce(
       `Booking group saved. Updated stays for ${studentIds?.length || 0} student(s) and ${adultIds?.length || 0} adult(s).`,
       "success"
     );

      // ✅ if saved template differs from last applied template -> warn
      const applied = flightAppliedRef.current;
      const appliedSnap = applied || { in: null, out: null };

      const inDiff =
        !sameDate(snapBeforeSave.in.date, appliedSnap?.in?.date) ||
        !equal(snapBeforeSave.in.flightId, appliedSnap?.in?.flightId) ||
        !equal(snapBeforeSave.in.depart, appliedSnap?.in?.depart) ||
        !equal(snapBeforeSave.in.arrive, appliedSnap?.in?.arrive) ||
        !equal(snapBeforeSave.in.time, appliedSnap?.in?.time) ||
        !equal(snapBeforeSave.in.notes, appliedSnap?.in?.notes);

      const outDiff =
        !sameDate(snapBeforeSave.out.date, appliedSnap?.out?.date) ||
        !equal(snapBeforeSave.out.flightId, appliedSnap?.out?.flightId) ||
        !equal(snapBeforeSave.out.depart, appliedSnap?.out?.depart) ||
        !equal(snapBeforeSave.out.arrive, appliedSnap?.out?.arrive) ||
        !equal(snapBeforeSave.out.time, appliedSnap?.out?.time) ||
        !equal(snapBeforeSave.out.notes, appliedSnap?.out?.notes);

      setFlightSavedNotApplied({ in: inDiff, out: outDiff });

    } catch (e) {
      console.error("Failed to save group + meta", e);
      toastOnce("Failed to save booking group details.", "error");
    }
  };
  
  const [deletingStudents, setDeletingStudents] = useState(false);
  const [deletingAdults, setDeletingAdults] = useState(false);

  const [deletingGroup, setDeletingGroup] = useState(false);

  const handleDeleteStudents = async (studentIdsToDelete) => {
    if (!Array.isArray(studentIdsToDelete) || studentIdsToDelete.length === 0) return;

    setDeletingStudents(true);
    setBulkOperationActive(true);
    setBulkOperationStatus(`Deleting ${studentIdsToDelete.length} student(s)…`);
    try {
      await deleteStudentsBatch(token, studentIdsToDelete);

      toastOnce(`Deleted ${studentIdsToDelete.length} student(s).`, "success");
      setDirty(true);

      const nextStudentIds = (studentIds || []).filter(id => !studentIdsToDelete.includes(id));

      pruneStudents(studentIdsToDelete);
      invalidate(studentIdsToDelete);
      onMembersChanged?.(group.id, nextStudentIds, adultIds);
      await refresh();
      setTab("danger");
    } catch (e) {
      console.error("Failed deleting students", e);
      toastOnce(`Failed to delete students: ${e.message}`, "error");
    } finally {
      setDeletingStudents(false);
      setBulkOperationActive(false);
      setBulkOperationStatus("");
    }
  };

  const handleDeleteAdults = async (adultIdsToDelete) => {
    if (!Array.isArray(adultIdsToDelete) || adultIdsToDelete.length === 0) return;

    setDeletingAdults(true);
    setBulkOperationActive(true);
    setBulkOperationStatus(`Deleting ${adultIdsToDelete.length} adult(s)…`);
    try {
      await deleteAdultsBatch(token, adultIdsToDelete);

      toastOnce(`Deleted ${adultIdsToDelete.length} adult(s).`, "success");
      setDirty(true);

      const nextAdultIds = (adultIds || []).filter(id => !adultIdsToDelete.includes(id));

      pruneAdults(adultIdsToDelete);
      invalidateAdults(adultIdsToDelete);
      onMembersChanged?.(group.id, studentIds, nextAdultIds);
      await refresh();
      setTab("danger");
    } catch (e) {
      console.error("Failed deleting adults", e);
      toastOnce(`Failed to delete adults: ${e.message}`, "error");
    } finally {
      setDeletingAdults(false);
      setBulkOperationActive(false);
      setBulkOperationStatus("");
    }
  };

  const handleDeleteGroupAndStudents = async () => {
    setDeletingGroup(true);
    setBulkOperationActive(true);
    try {
      const allStudentIds = studentsForDangerZone.map((s) => s.id).filter(Boolean);
      const allAdultIds = adultsForDangerZone.map((a) => a.id).filter(Boolean);

      if (allStudentIds.length) {
        setBulkOperationStatus(`Deleting ${allStudentIds.length} student(s)…`);
        await deleteStudentsBatch(token, allStudentIds);
      }

      if (allAdultIds.length) {
        setBulkOperationStatus(`Deleting ${allAdultIds.length} adult(s)…`);
        await deleteAdultsBatch(token, allAdultIds);
      }

      setBulkOperationStatus("Deleting group…");
      await fetchJson(`${SERVER_URL}api/campgroup/delete/${group.id}`, token, { method: "DELETE" });

      toastOnce("Group and all members deleted.", "success");
      onUpdated?.();
      handleClose();
    } catch (e) {
      console.error("Failed deleting group + members", e);
      toastOnce(`Failed to delete group and members: ${e.message}`, "error");
    } finally {
      setDeletingGroup(false);
      setBulkOperationActive(false);
      setBulkOperationStatus("");
    }
  };

  // parse and call add handlers
  const handleAddStudents = () => {
    const ids = addStudentsInput
      .split(/[, \n]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map(Number)
      .filter(Number.isInteger);
    
    if (!ids.length) return;
    
    addStudents(ids, meta)
      .then(() => {
        setAddStudentsInput("");
        setDirty(true);
      })
      .catch((e) => {
        console.error("Failed adding students", e);
        alert("Failed to add some students.");
      });
  };

  const handleAddAdults = () => {
    const ids = addAdultsInput.split(/[, \n]+/).map((s) => s.trim()).filter(Boolean).map(Number).filter(Number.isInteger);
    if (!ids.length) return;
    addAdults(ids, meta)
      .then(() => {
        setAddAdultsInput("");
        setDirty(true);
      })
      .catch((e) => {
        console.error("Failed adding adults", e);
        alert("Failed to add some adults.");
      });
  };

  const handleAlignBookingProducts = async () => {

    // Make sure meta changes are persisted before aligning
    if (!isValidCampus(meta.defaultCampus)) {
      setCampusError("Please select a campus from the list.");
      return;
    }
    setCampusError("");

    toastOnce("Aligning bookings…", "info");
    setDirty(true);

    if ((!studentIds?.length) && (!adultIds?.length)) {
      toastOnce("No students or adults in the group.", "info");
      return;
    }

    const token = sessionStorage.getItem("bearer");

    try {
      setAligning(true);
      toastOnce("Aligning bookings…", "info");

      const result = await alignBookings(token, group.id, {
        studentIds,
        adultIds,
        meta: {
          defaultCampus: meta.defaultCampus,
          defaultStartDate: meta.defaultStartDate,
          defaultEndDate: meta.defaultEndDate,
          defaultRate: meta.defaultRate,
        },
      });

      const r = result?.residential || {};
      const sw = result?.studentWeekend || {};
      const a = result?.activities || {};
      const ar = result?.adultResidential || {};
      const aw = result?.adultWeekend || {};

      const anyChanges =
        r.created > 0 || r.cancelled > 0 || r.updated > 0 ||
        sw.created > 0 || sw.cancelled > 0 ||
        ar.created > 0 || ar.cancelled > 0 || ar.updated > 0 ||
        aw.created > 0 ||
        a.updated > 0 || a.cancelled > 0 || a.migrated > 0;

      const anyIssues = (r.gaps || 0) > 0 || (ar.gaps || 0) > 0 || (a.warnings || 0) > 0;

      let severity = "info";
      if (anyIssues) severity = "warning";
      else if (anyChanges) severity = "success";

      const messageParts = [];
      if (r.created) messageParts.push(`${r.created} residential created.`);
      if (r.cancelled) messageParts.push(`${r.cancelled} residential cancelled.`);
      if (r.updated) messageParts.push(`${r.updated} residential updated.`);
      if (sw.created) messageParts.push(`${sw.created} weekend created.`);
      if (sw.cancelled) messageParts.push(`${sw.cancelled} weekend cancelled.`);
      if (ar.created) messageParts.push(`${ar.created} adult residential created.`);
      if (ar.cancelled) messageParts.push(`${ar.cancelled} adult residential cancelled.`);
      if (ar.updated) messageParts.push(`${ar.updated} adult residential updated.`);
      if (ar.gaps) messageParts.push(`${ar.gaps} adult residential gaps.`);
      if (aw.created) messageParts.push(`${aw.created} adult weekend created.`);
      if (r.gaps) messageParts.push(`${r.gaps} residential gaps.`);
      if (a.updated) messageParts.push(`${a.updated} activities updated.`);
      if (a.cancelled) messageParts.push(`${a.cancelled} activities cancelled.`);
      if (a.migrated) messageParts.push(`${a.migrated} activities migrated.`);
      if (a.warnings) messageParts.push(`${a.warnings} activity warnings.`);

      if (messageParts.length === 0) messageParts.push("Alignment complete — no changes required.");

      setAlignmentSummary({
        createdResidentialTotal: r.created,
        cancelledResidentialTotal: r.cancelled,
        updatedResidentialTotal: r.updated,
        weekendCreatedTotal: sw.created,
        weekendCancelledTotal: sw.cancelled,
        adultWeekendCreatedTotal: aw.created,
        adultWeekendCancelledTotal: 0,
        gapTotal: r.gaps,
        activityUpdatedTotal: a.updated,
        activityCancelledTotal: a.cancelled,
        activityMigratedTotal: a.migrated,
        activityWarningsTotal: a.warnings,
        severity,
        message: messageParts.join(" "),
      });

      toastFinal(messageParts.join(" "), severity);

      setHasEverAligned(true);
      setLastAlignedMeta(snapshotForAlignment(meta));
      bookingSettingsTouchedRef.current = false;

    } catch (err) {
      console.error(err);
      const msg = `Error aligning bookings: ${err.message}`;
      toastOnce(msg, "error");
      setAlignmentSummary({
        createdResidentialTotal: 0,
        gapTotal: 0,
        activityUpdatedTotal: 0,
        activityCancelledTotal: 0,
        activityMigratedTotal: 0,
        activityWarningsTotal: 0,
        severity: "error",
        message: msg,
      });
    } finally {
      setAligning(false);
    }
  };

  const alignmentState = useMemo(() => {
    if (metaLoading) return "loading";

    // baseline not established yet
    if (!lastAlignedMeta) return "initial";

    // user changed something since baseline
    if (metaNeedsAlignment) return "needs_align";

    // aligned successfully in this session
    if (hasEverAligned) return "aligned";

    // baseline exists and no changes since open
    return "up_to_date";
  }, [metaLoading, lastAlignedMeta, metaNeedsAlignment, hasEverAligned]);

  const applyGroupFlight = async (direction) => {
    try {
      // ✅ snapshot BEFORE save+apply (prevents stale meta issues)
      const snapBeforeApply = snapshotFromMeta(meta);

      await saveMeta({
        groupName,
        notes,
        studentIdsForStaySync: [],
        adultIdsForStaySync: [],
      });

      await fetchJson(
        `${SERVER_URL}api/campgroup/${group.id}/apply-group-flight?direction=${direction}&includeAdults=true`,
        token,
        { method: "POST" }
      );

      // ✅ now transfers should match this snapshot
      flightAppliedRef.current = flightAppliedRef.current || { in: null, out: null };

      if (direction === "IN") {
        flightAppliedRef.current = { ...flightAppliedRef.current, in: snapBeforeApply.in };
        setFlightSavedNotApplied((prev) => ({ ...prev, in: false }));
      } else {
        flightAppliedRef.current = { ...flightAppliedRef.current, out: snapBeforeApply.out };
        setFlightSavedNotApplied((prev) => ({ ...prev, out: false }));
      }

      toastOnce(`Applied ${direction} group flight`, "success");
      setDirty(true);
    } catch (e) {
      console.error(e);
      toastOnce(`Failed to apply ${direction} group flight`, "error");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xl"
      fullScreen={isSmall}
      PaperProps={{
        sx: { width: "96vw", maxWidth: "96vw", height: "90vh", m: 0 },
      }}
    >
      <DialogTitle>Edit Booking Group</DialogTitle>
      <DialogContent
        sx={{
          pt: 0,
          height: "calc(90vh - 120px)",
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            bgcolor: "background.paper",
            pt: 1,
          }}
        >
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab value="details" label="Details" />
            <Tab value="members" label="Members" />
            <Tab value="stays" label="Stays" />
            <Tab value="bookings" label="Bookings" />
            <Tab value="adultDetails" label="Adult details" />
            <Tab value="studentDetails" label="Student details" />
            {/* Weekly activities hidden — superseded by the Bookings tab. Restore this
                Tab to bring it back; the render block below is left intact. */}
            {/* <Tab value="weeklyActivities" label="Weekly activities" /> */}
            {showFinancials && <Tab value="finance" label="Finance" />}
            <Tab value="danger" label="Danger zone" />
          </Tabs>
          <Box sx={{ height: 8 }} />
        </Box>

        {/* DETAILS TAB */}
        {tab === "details" && (
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
            <Stack spacing={2}>
              {meta.hubspotDealId ? (
                <MuiAlert severity="info" variant="outlined" icon={false}>
                  Imported from HubSpot
                  {meta.hubspotImportedAt
                    ? ` on ${new Date(meta.hubspotImportedAt).toLocaleString("en-GB", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}`
                    : ""}
                  {" · deal "}
                  {meta.hubspotDealUrl ? (
                    <Link href={meta.hubspotDealUrl} target="_blank" rel="noopener noreferrer">
                      {meta.hubspotDealId}
                      <OpenInNewIcon sx={{ fontSize: 14, ml: 0.25, verticalAlign: "text-bottom" }} />
                    </Link>
                  ) : (
                    meta.hubspotDealId
                  )}
                  . Check the students, bookings and agent, then set the status.
                </MuiAlert>
              ) : null}

              <TextField
                label="Group Name"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                required
                fullWidth
              />

              <TextField
                label="Notes (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                multiline
                minRows={2}
                fullWidth
              />

              <FormControl fullWidth>
                <InputLabel id="group-status-label">Status</InputLabel>
                <Select
                  labelId="group-status-label"
                  label="Status"
                  disabled={metaLoading}
                  value={meta.status ?? ""}              // ✅ from booking meta
                  onChange={(e) => setMeta(m => ({ ...m, status: e.target.value }))}   // ✅ update booking meta
                >
                  <MenuItem value=""><em>—</em></MenuItem>
                  {BOOKING_STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                  {/* A value from before the current list stays selectable rather than blanking the field. */}
                  {meta.status && !BOOKING_STATUS_OPTIONS.some((o) => o.value === meta.status) ? (
                    <MenuItem value={meta.status}>{meta.status}</MenuItem>
                  ) : null}
                </Select>
              </FormControl>

              <GroupAgentSection
                groupName={groupName}
                studentIds={studentIds}
                meta={meta}
                token={token}
              />

              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2">Booking settings</Typography>

              {/* Validated default campus — used for BOTH campgroup.campus and meta.defaultCampus */}
              <FormControl fullWidth error={!!campusError}>
                <InputLabel id="meta-campus-label">Default Campus</InputLabel>
                <Select
                  labelId="meta-campus-label"
                  label="Default Campus"
                  value={meta.defaultCampus}
                  onChange={(e) => {
                    bookingSettingsTouchedRef.current = true;
                    setMeta((m) => ({ ...m, defaultCampus: e.target.value }));
                  }}
                  required
                >
                  {campusNames.length === 0 ? (
                    <MenuItem value="" disabled>No campuses available</MenuItem>
                  ) : (
                    campusNames.map((name) => (
                      <MenuItem key={name} value={name}>{name}</MenuItem>
                    ))
                  )}
                </Select>
                {campusError && <FormHelperText>{campusError}</FormHelperText>}
              </FormControl>

              <Stack direction="row" spacing={1}>
                <DatePicker
                  label="Default Start Date"
                  value={meta.defaultStartDate}
                  onChange={(v) => {
                    bookingSettingsTouchedRef.current = true;
                    setMeta((m) => ({ ...m, defaultStartDate: v }));
                  }}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <DatePicker
                  label="Default End Date"
                  value={meta.defaultEndDate}
                  onChange={(v) => {
                    bookingSettingsTouchedRef.current = true;
                    setMeta((m) => ({ ...m, defaultEndDate: v }));
                  }}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Stack>

              <TextField
                label="Default Nationality"
                value={meta.defaultNationality}
                onChange={(e) => {
                  bookingSettingsTouchedRef.current = true;
                  setMeta((m) => ({ ...m, defaultNationality: e.target.value }));
                }}
                fullWidth
              />

              {/* The group rate is set by the back office; the server ignores it otherwise. */}
              {showFinancials && (
                <TextField
                  label="Default Rate"
                  type="number"
                  inputProps={{ step: "0.01", min: "0" }}
                  value={meta.defaultRate}
                  onChange={(e) => {
                    bookingSettingsTouchedRef.current = true;
                    setMeta((m) => ({ ...m, defaultRate: e.target.value }));
                  }}
                  fullWidth
                />
              )}

              <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2">Group flights</Typography>

                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    {/* LEFT: INBOUND */}
                    <Grid item xs={12} md={6}>
                      <Box sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                          Inbound (IN)
                        </Typography>

                        {/* 2 columns inside card */}
                        <Grid container spacing={1}>
                          <Grid item xs={12} sm={6}>
                            <DatePicker
                              label="Flight date"
                              value={meta.inTransferDate}
                              onChange={(v) => setMeta((m) => ({ ...m, inTransferDate: v }))}
                              slotProps={{ textField: { fullWidth: true } }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Flight number"
                              value={meta.inFlightId}
                              onChange={(e) =>
                                setMeta((m) => ({ ...m, inFlightId: e.target.value.toUpperCase() }))
                              }
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="From (airport)"
                              value={meta.inDepart}
                              onChange={(e) => setMeta((m) => ({ ...m, inDepart: e.target.value }))}
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="To (airport)"
                              value={meta.inArrive}
                              onChange={(e) => setMeta((m) => ({ ...m, inArrive: e.target.value }))}
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Arrival time (HH:MM)"
                              value={meta.inArrivalTime}
                              onChange={(e) => setMeta((m) => ({ ...m, inArrivalTime: e.target.value }))}
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Notes"
                              value={meta.inNotes}
                              onChange={(e) => setMeta((m) => ({ ...m, inNotes: e.target.value }))}
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Button
                              variant="contained"
                              onClick={() => applyGroupFlight("IN")}
                              disabled={!meta.inTransferDate}
                            >
                              Apply IN to group
                            </Button>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>

                    {/* RIGHT: OUTBOUND */}
                    <Grid item xs={12} md={6}>
                      <Box sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                          Outbound (OUT)
                        </Typography>

                        {/* 2 columns inside card */}
                        <Grid container spacing={1}>
                          <Grid item xs={12} sm={6}>
                            <DatePicker
                              label="Flight date"
                              value={meta.outTransferDate}
                              onChange={(v) => setMeta((m) => ({ ...m, outTransferDate: v }))}
                              slotProps={{ textField: { fullWidth: true } }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Flight number"
                              value={meta.outFlightId}
                              onChange={(e) =>
                                setMeta((m) => ({ ...m, outFlightId: e.target.value.toUpperCase() }))
                              }
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="From (airport)"
                              value={meta.outDepart}
                              onChange={(e) => setMeta((m) => ({ ...m, outDepart: e.target.value }))}
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="To (airport)"
                              value={meta.outArrive}
                              onChange={(e) => setMeta((m) => ({ ...m, outArrive: e.target.value }))}
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Departure time (HH:MM)"
                              value={meta.outDepartureTime}
                              onChange={(e) =>
                                setMeta((m) => ({ ...m, outDepartureTime: e.target.value }))
                              }
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Notes"
                              value={meta.outNotes}
                              onChange={(e) => setMeta((m) => ({ ...m, outNotes: e.target.value }))}
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Button
                              variant="contained"
                              onClick={() => applyGroupFlight("OUT")}
                              disabled={!meta.outTransferDate}
                            >
                              Apply OUT to group
                            </Button>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
  
                  </Grid>
                  {(flightSavedNotApplied.in || flightSavedNotApplied.out) && (
                    <MuiAlert severity="warning" variant="outlined" sx={{ mt: 1 }}>
                      Flight details have been saved to the group, but not yet applied to member transfers.
                      {flightSavedNotApplied.in && <> <strong>Inbound</strong> needs applying.</>}
                      {flightSavedNotApplied.out && <> <strong>Outbound</strong> needs applying.</>}
                    </MuiAlert>
                  )}

                  {/* Inline guidance / status box below rate */}
                  <Box sx={{ mt: 1 }}>
                    {!studentIds.length && (
                      <MuiAlert severity="info" variant="outlined">
                        This group has no students yet. Add students on the <strong>Members</strong> tab,
                        then return here to align residential bookings.
                      </MuiAlert>
                    )}

                    {studentIds.length > 0 && !isValidCampus(meta.defaultCampus) && (
                      <MuiAlert severity="warning" variant="outlined">
                        Set a <strong>Default Campus</strong> (and dates if possible), then click{" "}
                        <strong>Save</strong>. Once your booking settings are correct, use{" "}
                        <strong>Align Bookings</strong> to update student bookings.
                      </MuiAlert>
                    )}

                    {studentIds.length > 0 &&
                      isValidCampus(meta.defaultCampus) &&
                      alignmentState === "needs_align" && (
                        <MuiAlert severity="warning" variant="outlined">
                          Changes have been made to the booking settings that are not currently
                          reflected in existing bookings. Click <strong>Align Bookings</strong> below
                          to bring bookings up to date.
                        </MuiAlert>
                      )}

                    {studentIds.length > 0 &&
                      isValidCampus(meta.defaultCampus) &&
                      alignmentState === "aligned" && (
                        <MuiAlert severity="success" variant="outlined">
                          Bookings were successfully aligned with the current booking settings.
                          You can run <strong>Align Bookings</strong> again at any time.
                        </MuiAlert>
                      )}

                    {/* 🔹 Persistent result of the last alignment run */}
                    {alignmentSummary && (
                      <Box sx={{ mt: 1 }}>
                        <MuiAlert severity={alignmentSummary.severity} variant="standard">
                          {alignmentSummary.message}
                          {alignmentSummary.gapTotal > 0 && (
                            <span>
                              {" "}
                              You can review and manually resolve these residential coverage gaps in the
                              students’ booking records.
                            </span>
                          )}
                          {(alignmentSummary.activityUpdatedTotal > 0 ||
                            alignmentSummary.activityCancelledTotal > 0 ||
                            alignmentSummary.activityMigratedTotal > 0) && (
                            <div>
                              Activity changes:&nbsp;
                              {alignmentSummary.activityUpdatedTotal > 0 && (
                                <span>{alignmentSummary.activityUpdatedTotal} updated. </span>
                              )}
                              {alignmentSummary.activityCancelledTotal > 0 && (
                                <span>{alignmentSummary.activityCancelledTotal} cancelled. </span>
                              )}
                              {alignmentSummary.activityMigratedTotal > 0 && (
                                <span>{alignmentSummary.activityMigratedTotal} migrated. </span>
                              )}
                            </div>
                          )}
                        {alignmentSummary.activityWarningsTotal > 0 && (
                          <div>
                            {alignmentSummary.activityWarningsTotal} activity warnings were generated.
                              Check the console or logs for details.
                          </div>
                        )}
                      </MuiAlert>
                    </Box>
                  )}
                </Box>

                {metaLoading && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                    <CircularProgress size={18} /> <span>Loading booking settings…</span>
                  </Box>
                )}
            </Stack>
          </LocalizationProvider>
        )}

        {/* MEMBERS TAB */}
        {tab === "members" && (
          <MembersTab
            loading={loadingMembers}
            studentIds={studentIds}
            studentDetails={studentDetails}
            adultIds={adultIds}
            adultDetails={adultDetails}
            onAddStudents={handleAddStudents}
            onRemoveStudent={handleRemoveStudent}
            onAddAdults={handleAddAdults}
            onRemoveAdult={handleRemoveAdult}
            addStudentsInput={addStudentsInput}
            setAddStudentsInput={setAddStudentsInput}
            addAdultsInput={addAdultsInput}
            setAddAdultsInput={setAddAdultsInput}
            newStudentsCount={newStudentsCount}
            setNewStudentsCount={setNewStudentsCount}
            onAddNewStudents={handleAddNewStudents}
            newAdultsCount={newAdultsCount}
            setNewAdultsCount={setNewAdultsCount}
            onAddNewAdults={handleAddNewAdults}
            creatingStudents={creatingStudents}
            creatingAdults={creatingAdults}
          />
        )}

        {/* ADULT DETAILS TAB */}
        {tab === "adultDetails" && (
          <AdultDetailsTab
            loading={loadingMembers}
            adultDetails={adultDetails}
            editable={editableAdults}
            showToast={toastOnce}
          />
        )}

        {/* STUDENT DETAILS TAB */}
        {tab === "studentDetails" && (
          <StudentDetailsTab
            loading={loadingMembers}
            studentDetails={studentDetails}
            editable={editable}
            showToast={toastOnce}
            groupId={group?.id}
            groupName={groupName}
            token={token}
            arrivalDate={meta?.defaultStartDate ? fmtDate(meta.defaultStartDate) : ""}
            onCsvApplied={async () => {
              // Members may have been added or a placeholder replaced — re-read
              // the membership from the server and tell the list.
              setDirty(true);
              try {
                const m = await reloadMembership();
                onMembersChanged?.(group.id, m.studentIds, m.adultIds);
              } catch (e) {
                toastOnce("Uploaded, but the member list couldn't be refreshed — close and reopen the group", "warning");
              }
            }}
            onGoToBookings={() => setTab("bookings")}
          />
        )}

        {/* WEEKLY ACTIVITIES TAB */}
          {tab === "stays" && (
            <StaysTab
              token={token}
              showToast={toastOnce}
              studentIds={studentIds}
              adultIds={adultIds}
              meta={meta}
            />
          )}

          {tab === "bookings" && (
            <BookingsTab
              group={group}
              token={token}
              showToast={toastOnce}
              studentIds={studentIds}
              adultIds={adultIds}
              meta={meta}
              showFinancials={showFinancials}
            />
          )}

          {tab === "weeklyActivities" && (
            <WeeklyActivitiesTab
              students={studentsForActivities}
              showToast={toastOnce}
            />
          )}

          {tab === "finance" && showFinancials && (
            <GroupFinanceTab
              group={group}
              token={token}
              showToast={toastOnce}
              studentIds={studentIds}
              adultIds={adultIds}
            />
          )}

        {/* DANGER ZONE */}
          {tab === "danger" && (
            <DangerZone
              students={studentsForDangerZone}
              adults={adultsForDangerZone}
              loading={dzLoading}
              loadingAdults={dzAdultsLoading}
              deletingStudents={deletingStudents}
              deletingAdults={deletingAdults}
              deletingGroup={deletingGroup}
              onDeleteStudents={handleDeleteStudents}
              onDeleteAdults={handleDeleteAdults}
              onDeleteGroupAndStudents={handleDeleteGroupAndStudents}
            />
          )}

      </DialogContent>
      <DialogActions>

        {tab === "details" ? (
          <>
      
          <Button
            onClick={handleSaveDetails}
            variant="contained"
            disabled={!canSave || metaLoading || aligning}
          >
            {metaLoading ? "Saving…" : "Save"}
          </Button>

          <Tooltip
            title={
              ALIGN_BOOKINGS_ENABLED
                ? ""
                : "Disabled until the backend weekend-booking fix (one excursion per weekend). Book manually on the Bookings tab."
            }
          >
            {/* span wrapper so the tooltip still shows while the button is disabled */}
            <span>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleAlignBookingProducts}
                disabled={
                  !ALIGN_BOOKINGS_ENABLED ||
                  aligning ||
                  (!studentIds.length && !adultIds.length) ||
                  !isValidCampus(meta.defaultCampus)
                }
              >
                {aligning ? (
                  <>
                  <CircularProgress size={18} sx={{ mr: 1 }} />
                    Aligning Bookings…
                  </>
                ) : (
                  "Align Bookings"
                )}
              </Button>
            </span>
          </Tooltip>

          <Button onClick={handleClose} disabled={aligning}>
            Close
          </Button>
          </>
        ) : (
          <Button onClick={handleClose} disabled={aligning}>
            Close
          </Button>
        )}
      </DialogActions>

      {/* lightweight toast */}
      <Snackbar
        key={toastMsg}
        open={toastOpen}
        autoHideDuration={2000}
        onClose={(_, reason) => {
          if (reason === "clickaway") return;
            setToastOpen(false);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
        <MuiAlert elevation={6} variant="filled" severity={toastSeverity} onClose={() => setToastOpen(false)}>
          {toastMsg}
        </MuiAlert>
      </Snackbar>

      {/* Confirmation dialog for bulk create */}
      <Dialog open={confirmCreateOpen} onClose={() => setConfirmCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Add {confirmCreateCount} new {confirmCreateType === "students" ? "student" : "adult"}
          {confirmCreateCount !== 1 ? "s" : ""} to this group?
        </DialogTitle>
        <DialogActions>
          <Button onClick={() => setConfirmCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmCreate}>OK</Button>
        </DialogActions>
      </Dialog>

      {/* Full-page overlay during bulk operations */}
      <Backdrop
        open={bulkOperationActive}
        sx={{
          position: "absolute",
          zIndex: (theme) => theme.zIndex.modal + 1,
          bgcolor: "rgba(255,255,255,0.85)",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="body1" sx={{ color: "text.primary", fontWeight: 500 }}>
          {bulkOperationStatus}
        </Typography>
        {bulkElapsed >= 60 && (
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
            Still working on it — this can take a while for large groups…
          </Typography>
        )}
      </Backdrop>
    </Dialog>
  );
}